import { BaseSqliteRepository } from './base-sqlite.repository';
import { SqliteQueryError } from '../../errors';
import type { IDebtorRepository } from '@app/domain/ports';
import type { Debtor } from '@app/domain/entities';
import type { DebtorFilter, DebtorCursor } from '@app/domain/ports';

interface DebtorRow {
  id: number;
  date: number; // Unix timestamp in seconds
  accountId: number;
  accountName: string;
  categoryId: number;
  categoryName: string;
  currencyCode: string;
  amount: number;
  totalAmount: number;
  exchangeRate: number;
  isDebtClosed: number; // SQLite boolean (0 or 1)
  note: string;
}

export class DebtorRepository extends BaseSqliteRepository implements IDebtorRepository {
  private readonly BASE_FROM = `
    FROM debtors d
    INNER JOIN accounts a ON d.account_id = a._id
    INNER JOIN categories cat ON d.category_id = cat._id
    INNER JOIN currencies c ON d.currency_id = c._id
    INNER JOIN available_currencies AS cl ON c.available_currency_id = cl._id
    WHERE d.is_deleted = 0
  `;

  private readonly BASE_FIELDS = [
    'd._id as id',
    'd.debtor_date as date',
    'd.account_id as accountId',
    'a.name as accountName',
    'd.category_id as categoryId',
    'cat.name as categoryName',
    'cl.ticker as currencyCode',
    'd.amount as amount',
    'd.total_amount as totalAmount',
    'd.exchange_rate as exchangeRate',
    'd.is_debt_closed as isDebtClosed',
    'd.note as note',
  ];

  private readonly COUNT_QUERY = `
    SELECT COUNT(*) AS total
    ${this.BASE_FROM}
  `;

  async count(filter?: DebtorFilter): Promise<number> {
    const { query, params } = this.applyFilter(this.COUNT_QUERY, filter);
    const result = await this.queryOne<{ total: number }>(query, params);
    return result?.total ?? 0;
  }

  async findAll(cursor: DebtorCursor, filter?: DebtorFilter): Promise<Debtor[]> {
    const sql = `
      SELECT
        ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
    `;

    // eslint-disable-next-line prefer-const
    let { query, params } = this.applyFilter(sql, filter);

    query += ` ORDER BY d.debtor_date DESC, d._id DESC LIMIT ? OFFSET ?`;
    params.push(cursor.limit, cursor.offset ?? 0);

    const rows = await this.query<DebtorRow>(query, params);
    return rows.map((row) => this.mapRowToEntity(row));
  }

  async getById(id: number): Promise<Debtor | null> {
    const query = `
      SELECT
      ${this.BASE_FIELDS.join(',\n ')}
      ${this.BASE_FROM}
       AND d._id = ?`;
    const row = await this.queryOne<DebtorRow>(query, [id]);
    return row ? this.mapRowToEntity(row) : null;
  }

  async insert(debtor: Omit<Debtor, 'id' | 'accountName' | 'debtorName'>): Promise<number> {
    const date = new Date(debtor.date).setUTCHours(0, 0, 0, 0) / 1000;

    const result = await this.queryOne<{ id: number }>(
      `INSERT INTO debtors (debtor_date, account_id, category_id, amount, total_amount, currency_id, exchange_rate, is_debt_closed, note)
       VALUES (?, ?, ?, ?, ?, (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?), ?, ?, ?)
       RETURNING _id AS id`,
      [
        date,
        debtor.accountId,
        debtor.debtorId,
        debtor.amount,
        debtor.totalAmount,
        debtor.currencyCode,
        debtor.exchangeRate,
        debtor.isDebtClosed ? 1 : 0,
        debtor.note,
      ],
    );

    if (!result?.id) {
      throw new SqliteQueryError('INSERT INTO debtors', [], 'Failed to insert debtor');
    }

    return result.id;
  }

  async update(debtor: Omit<Debtor, 'accountName' | 'debtorName'>): Promise<void> {
    const date = new Date(debtor.date).setUTCHours(0, 0, 0, 0) / 1000;

    await this.execute(
      `UPDATE debtors
       SET debtor_date = ?,
           account_id = ?,
           category_id = ?,
           amount = ?,
           total_amount = ?,
           currency_id = (SELECT c._id FROM currencies c INNER JOIN available_currencies cl ON c.available_currency_id = cl._id WHERE cl.ticker = ?),
           exchange_rate = ?,
           is_debt_closed = ?,
           note = ?,
           updated_at = strftime('%s','now')
       WHERE _id = ?`,
      [
        date,
        debtor.accountId,
        debtor.debtorId,
        debtor.amount,
        debtor.totalAmount,
        debtor.currencyCode,
        debtor.exchangeRate,
        debtor.isDebtClosed ? 1 : 0,
        debtor.note,
        debtor.id,
      ],
    );
  }

  async updateTotalAmount(id: number, totalAmount: number): Promise<void> {
    await this.execute(
      `UPDATE debtors
       SET total_amount = ?,
           is_debt_closed = CASE WHEN ? >= amount THEN 1 ELSE 0 END,
           updated_at = strftime('%s','now')
       WHERE _id = ?`,
      [totalAmount, totalAmount, id],
    );
  }

  async refreshDebtClosed(id: number): Promise<boolean> {
    // Only reopen — never auto-close; user decides that
    await this.execute(
      `UPDATE debtors
       SET is_debt_closed = CASE WHEN total_amount < amount THEN 0 ELSE is_debt_closed END,
           updated_at = strftime('%s','now')
       WHERE _id = ?`,
      [id],
    );

    // Return true when closing should be suggested (paid back >= principal, but not yet closed)
    const result = await this.queryOne<{ suggest: number }>(
      `SELECT CASE WHEN total_amount >= amount AND is_debt_closed = 0 THEN 1 ELSE 0 END AS suggest
       FROM debtors WHERE _id = ?`,
      [id],
    );
    return result?.suggest === 1;
  }

  async setDebtClosed(id: number, closed: boolean): Promise<void> {
    await this.execute(`UPDATE debtors SET is_debt_closed = ?, updated_at = strftime('%s','now') WHERE _id = ?`, [
      closed ? 1 : 0,
      id,
    ]);
  }

  async delete(id: number): Promise<void> {
    await this.execute("UPDATE debtors SET is_deleted = 1, updated_at = strftime('%s','now') WHERE _id = ?", [id]);
  }

  private applyFilter(
    query: string,
    filter?: DebtorFilter,
    params: unknown[] = [],
  ): { query: string; params: unknown[] } {
    if (filter?.accountId) {
      query += ` AND d.account_id = ?`;
      params.push(filter.accountId);
    }

    if (filter?.debtorId) {
      query += ` AND d.category_id = ?`;
      params.push(filter.debtorId);
    }

    if (filter?.isDebtClosed !== undefined) {
      query += ` AND d.is_debt_closed = ?`;
      params.push(filter.isDebtClosed ? 1 : 0);
    }

    if (filter?.dateFrom) {
      query += ` AND d.debtor_date >= ?`;
      params.push(new Date(filter.dateFrom).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.dateTo) {
      query += ` AND d.debtor_date < ?`;
      params.push(new Date(filter.dateTo).setUTCHours(0, 0, 0, 0) / 1000);
    }

    if (filter?.currencyCode) {
      query += ` AND cl.ticker = ?`;
      params.push(filter.currencyCode);
    }

    if (filter?.hasOutstandingAmount !== undefined) {
      query += filter.hasOutstandingAmount ? ` AND d.amount > d.total_amount` : ` AND d.amount <= d.total_amount`;
    }

    return { query, params };
  }

  async countByAccount(accountId: number): Promise<number> {
    const result = await this.queryOne<{ count: number }>(
      `SELECT COUNT(*) as count FROM debtors WHERE account_id = ? AND is_deleted = 0`,
      [accountId],
    );
    return result?.count ?? 0;
  }

  async bulkUpdateAccount(sourceAccountId: number, targetAccountId: number): Promise<void> {
    await this.execute(
      `UPDATE debtors
       SET account_id = ?,
           updated_at = strftime('%s','now')
       WHERE account_id = ? AND is_deleted = 0`,
      [targetAccountId, sourceAccountId],
    );
  }

  private mapRowToEntity(row: DebtorRow): Debtor {
    return {
      id: row.id,
      date: new Date(row.date * 1000),
      accountId: row.accountId,
      accountName: row.accountName,
      debtorId: row.categoryId,
      debtorName: row.categoryName,
      currencyCode: row.currencyCode,
      amount: row.amount,
      totalAmount: row.totalAmount,
      exchangeRate: !row.exchangeRate ? 1 : row.exchangeRate,
      isDebtClosed: row.isDebtClosed === 1,
      note: row.note,
    };
  }
}
